import useHttpService from '../shared/services/http-service';
import { InitialData } from './root-types';
import { InitialUser } from '../user/user-types';
import { useRootStore } from './root-store';
import { useUserStore } from '../user/user-store';
import { usePermissionStore } from '../permission/permission-store';
import i18n from '../../plugins/i18n';
import moment from 'moment';
import 'moment/locale/fr';
import useFeatureFlag from '../feature-flag/use-feature-flag';
import { useListStore } from '../lists/list-store';
import { useListStore as  useMentorListStore} from '../lists/mentor-list-store';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useRoot() {
  const { httpGet } = useHttpService();
  const rootStore = useRootStore();
  const listStore = useListStore();
  const mentorListStore = useMentorListStore();
  const userStore = useUserStore();
  const permissionStore = usePermissionStore();
  const { initFlags } = useFeatureFlag();

  const initialQuery = async (): Promise<InitialData> => {
    return await httpGet<InitialData>('/initial-query');
  };

  const init = async (): Promise<void> => {
    const result: InitialData = await initialQuery();

    rootStore.setSiteSettings(result.settings);
    rootStore.setCompanies(result.companies);
    rootStore.setLocales(result.locales);
    rootStore.setObjectStates(result.objectStates);
    rootStore.setCountries(result.countries);
    rootStore.setRolesCanModifyProjects(result.rolesCanModifyProjects);
    rootStore.setGodModeRole(result.godModeRole);
    rootStore.setObjectClosedStates(result.objectClosedStates);
    rootStore.setUrgentTraffic(result.haveUrgentTraffic);
    rootStore.setAppLocales(result.appLocale);
    rootStore.setDefaultCurrency(result.defaultCurrency);
    rootStore.setCurrencyConfigs(result.currencyConfigs);
    rootStore.setHolidayTypes(result.holidayTypes);
    rootStore.setAllHolidayTypes(result.allHolidayTypes);
    rootStore.setPartnerTypes(result.partnerTypes);
    rootStore.setIsApprovalUser(result.isApprovalUser);
    rootStore.setTrafficAllowedDates(result.trafficAllowedDates);
    rootStore.setTrafficStates(result.trafficStates);
    listStore.setJobTitles(result.jobTitles);
    listStore.setTrafficAllowedJobTitles(result.trafficAllowedJobTitles);

    mentorListStore.setMentorTitles(result.mentorTitles);
    mentorListStore.setTrafficAllowedMentorTitles(result.trafficAllowedMentorTitles);

    permissionStore.setCurrentPermissions(result.user.permissions);
    permissionStore.setCurrentRoles(result.user.roles);

    const user: InitialUser = result.user as InitialUser;
    userStore.setUser(user);

    const { locale, setLocaleMessage } = i18n.global;
    locale.value = 'en';
    setLocaleMessage('en', result.translations);

    moment.updateLocale('en', result.momentsConfig);
    rootStore.setTrafficLock(result.trafficLocked);
    rootStore.setTrafficFinalized(result.trafficFinalized);

    await initFlags(user.email);
  };

  const getSiteSetting = (key: string): any => {
    const split = key.split('.');

    if (!split[0] && !split[1]) {
      throw new Error(`Wrong key format: ${key}`);
    }

    const setting = rootStore.siteSettings[split[0]][split[1]];

    if (!setting) {
      throw new Error(`Setting not exists: ${key}`);
    }

    return setting;
  };

  return {
    init,
    getSiteSetting,
  };
}
